import Vue from 'vue';
import VueRouter from 'vue-router';
import { sync } from 'vuex-router-sync';

Vue.use(VueRouter);

import EventBus from '@lib/EventBus';

import store from '@/store';

import Page_404 from '@/components/Page_404';
import Page_403 from '@/components/Page_403';
import PageError from '@/components/PageError';

import home from '@/router/home.js';
import doc from '@/router/doc.js';
import profile from '@/router/profile.js';
import project from '@/router/project.js';
import client from '@/router/client.js';
import video from '@/router/video.js';
import admin from '@/router/admin.js';
import contract from '@/router/contract.js';
import actors from '@/router/actors.js';
import clients from '@/router/clients.js';
import bible from '@/router/bible.js';

const router = new VueRouter({
    mode: 'history',
    routes: [
        ...home,
        ...doc,
        ...profile,
        ...project,
        ...client,
        ...video,
        ...admin,
        ...contract,
        ...actors,
        ...clients,
        ...bible,
        {
            name: '403',
            path: '*',
            component: Page_403,
            meta: { public: true },
        },
        {
            name: '404',
            path: '*',
            component: Page_404,
            meta: { public: true },
        },
        {
            name: 'error',
            path: '/error',
            component: PageError,
            meta: { public: true },
        },
    ],
});

router.beforeEach((to, from, next) => {
    // Set the menu Section between Actor profile or Admin pages
    if (to.meta.menuSection) {
        store.commit('setMenuSection', to.meta.menuSection);
    }

    if (to.name === 'home' && store.getters.getHasValidAccessToken) {
        next({ name: 'profile-voice' }); // Presmeruj na profile, ak je prihlásený
    }

    if (to.query.actorToken) store.commit('setActorAccessToken', to.query.actorToken);

    const reqRoles = to.meta.roles ?? [];
    const reqPerms = to.meta.permissions ?? [];
    const isPublicPage =
        to.meta.roles?.length === 0 && to.meta.permissions?.length === 0 && to.meta.public === undefined
            ? false
            : to.meta.public;
    const accessibleByToken = to.meta.accessibleByToken === undefined ? false : to.meta.accessibleByToken;
    const avoidTerms = to.meta.avoidTerms === undefined ? false : to.meta.avoidTerms;
    const isSetActorAccessToken = store.getters.getIsSetActorAccessToken;
    const authMethod = store.getters.getAuthMethod;
    const returnUrl = to.fullPath;

    store.dispatch('closeAllPopups');

    let silentAuthPromise;

    if (
        !isPublicPage &&
        authMethod == 'implicit' &&
        store.getters.getHasAccessToken &&
        !store.getters.getHasValidAccessToken
    ) {
        silentAuthPromise = store.dispatch('doSilentAuthorization');
    }

    const silentAuthFailed = (err) => {
        if (err == 'need_login') {
            return store.dispatch('openImplicitWindow');
        }
        EventBus.$emit('auth.login.error', err);
    };

    const nextWithTermsCheck = () => {
        if (!avoidTerms && store.getters.getUser && !store.getters.getUserTermsAgreed) {
            return next({ name: 'profile-vop', query: { returnUrl } });
        }
        next();
    };

    // Check if user has required roles
    if (store.getters.getHasValidAccessToken && reqRoles.length && !store.getters.hasRole(reqRoles)) {
        return next({ name: '403' });
    }

    // Check if user has required permissions
    if (store.getters.getHasValidAccessToken && reqPerms.length && !store.getters.hasPermissionTo(reqPerms)) {
        return next({ name: '403' });
    }

    if (isPublicPage || (accessibleByToken && isSetActorAccessToken) || store.getters.getHasValidAccessToken) {
        if (silentAuthPromise) {
            return silentAuthPromise.catch(silentAuthFailed).finally(() => {
                nextWithTermsCheck();
            });
        }
        return nextWithTermsCheck();
    }

    if (silentAuthPromise) {
        silentAuthPromise.catch(silentAuthFailed);
    } else {
        next({ name: 'home', query: { returnUrl } });
        store.dispatch('openAuthPopup');
    }

    store.dispatch('clearAuthData');

    return new Promise((resolve, reject) => {
        EventBus.$once('auth.login.success', () => resolve());
        EventBus.$once('auth.login.error', (err) => reject());
    })
        .then((result) => {
            next({ path: returnUrl });
        })
        .catch((err) => {
            next({ name: 'home', query: { returnUrl } });
        });
});

sync(store, router);

export default router;
